import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Card, Button, InputNumber, Select, message, Upload, Table,
  Tag, Space, Divider, Modal, Row, Col, Spin, Tooltip, Checkbox, Input,
} from 'antd';
import {
  UploadOutlined, DownloadOutlined, SendOutlined, DeleteOutlined,
  CheckCircleFilled, CloseCircleFilled,
} from '@ant-design/icons';
import api, { getApiBaseURL } from '../utils/api';
import { getToken } from '../utils/auth';
import { getDataForms, PHD_TYPES, isSubField } from '../utils/dataForms';
import { formatDateTime } from '../utils/timeUtils';

/**
 * Maps template base name (without 表模版/表模板 suffix) to field keys that should show it.
 * Covers all departments: 审计, 智能科学与技术, 生物与医药.
 */
const TEMPLATE_FIELD_MAP = {
  discipline_team: {
    '专任教师基本情况': ['专任教师人数'],
    '行业教师基本情况': ['行业教师人数'],
    '骨干教师情况': ['骨干教师人数', '骨干教师'],
    '骨干教师基本情况': ['骨干教师人数', '骨干教师'],
    '国家级人才': ['国家级人才', '新增国家级人才(定义见说明第五条)'],
    '国家级人才数量': ['国家级人才数量'],
    '审计相关省级人才': ['审计相关省级人才'],
    '省级人才数量': ['省级人才数量', '新增省级人才(定义见说明第五条)'],
    '博士生导师数量': ['博士生导师数量'],
    '学术骨干数量': ['学术骨干数量'],
    '学科带头人数量': ['学科带头人数量'],
  },
  scientific_research: {
    '发表高水平学术论文数': [
      '在CSSCI或SSCI上新增发表审计相关高水平学术论文(篇)',
      '在SCI、SSCI、CSSCI收录期刊和一级期刊(浙大标准)及以上期刊发表新增本学科论文数(篇)',
    ],
    '发表论文数': ['发表论文数'],
    'CNS发表论文数': ['CNS发表论文数', '在CNS上新增发表论文(含子刊)'],
    '国家级项目数': [
      '新增主持国家级重大、重点项目数', '新增国家级项目数',
    ],
    '审计相关省部级项目数': ['新增主持审计相关省部级项目数'],
    '省部级项目数': [
      '新增主持省部级项目数', '师均主持省部级及以上项目数',
      '省部级项目数',
    ],
    '师均审计相关科研成果': ['师均审计相关科研成果数(含发表学术论文、决策咨询报告、案例分析报告等)'],
    '年师均科研到账经费': ['年师均科研到账经费(万元)'],
    '年均科研到账总经费': ['年均科研到账总经费(万元)', '年均科研到账总经费'],
    '省部级科研成果奖': ['新增省部级科研成果奖'],
    '省部级及以上科研奖励': [
      '新增省部级科技奖二等奖及以上数', '省部级及以上科研奖励',
      '新增省部级及以上科研一等奖及以上奖项数',
    ],
    '省部级及以上奖励': ['省部级及以上奖励'],
    '授权发明专利': ['新增转化或应用的授权发明专利数'],
    '省部级及以上科研项目累计到账经费': ['省部级及以上科研项目累计到账经费(万元)'],
    '省部级科研平台': ['新增省部级科研平台', '新增国家或省部级科研平台'],
    '500万以上重大横向项目数': ['新增500万以上重大横向项目数', '500万以上重大横向项目数'],
  },
  talent_training: {
    '审计相关专业优秀硕士学位论文': ['省级及以上审计相关专业优秀硕士学位论文数'],
    '审计相关专业教学案例库': ['建立审计相关专业教学案例库数'],
    '省部级及以上教学案例': ['新增省部级及以上教学案例(篇)'],
    '省级规划教材、优秀教材': ['省级规划教材或优秀教材数'],
    '校级及以上教学名师': ['校级及以上教学名师'],
    '升博名单': ['升博率', '升博名单'],
    '年均硕士生参加科研项目': ['年均硕士生参加省级及以上科研项目数'],
    '年均硕士生科研成果': ['年均硕士生科研成果', '年均硕士生发表高水平学术论文、申请发明专利款'],
    '省级以上教学平台、实践基地和实验室数': ['新增省级以上教学平台、实践基地和实验室数'],
    '省级教学、科研团队': ['新增省级教学、科研团队数', '新增省级教学、科研团队'],
    '省级及以上教学成果奖': ['新增省级及以上教学成果奖', '新增省级及以上教学成果奖(定义见说明第五条, 含参与)'],
  },
  academic_exchange: {
    '参加校外组织审计相关活动': ['师均参加校外组织的审计相关活动次数'],
    '与境外高水平机构合作': ['与境外高水平机构开展合作项目数'],
    '主办、承办学术会议': ['新增主办、承办国际学术会议或全国学术会议(次)'],
    '参加学术会议': ['师均参加国内外学术会议次数'],
    '年均邀请境内外专家报告': ['年均邀请境内外专家报告次数'],
    '研究生获全额资助参加境内外学术交流': ['研究生新增获全额资助参加境内外学术交流人数'],
  },
};

/**
 * PhD authorization matrix entry component (combined view)
 *
 * Four sections (discipline_team, talent_training, scientific_research, academic_exchange)
 * merged into one table using rowSpan for the indicator type column.
 */
const PhdMatrixEntry = ({ department, submissions, onRefresh }) => {
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [yearInput, setYearInput] = useState(String(new Date().getFullYear()));
  const [cellValues, setCellValues] = useState({});
  const [cellProofs, setCellProofs] = useState({});
  const [uploading, setUploading] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [existingData, setExistingData] = useState({});
  const [selectedRows, setSelectedRows] = useState({}); // {dataType_fieldKey: true}
  const [proofModalVisible, setProofModalVisible] = useState(false);
  const [proofModalData, setProofModalData] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [templates, setTemplates] = useState({});
  const [templateLoading, setTemplateLoading] = useState(false);

  const dataForms = useMemo(() => getDataForms(department?.name || ''), [department?.name]);
  const phdDataForms = useMemo(
    () => dataForms.filter(f => PHD_TYPES.includes(f.key)),
    [dataForms]
  );

  // Map existing submission data by type and year
  useEffect(() => {
    if (!submissions || !department) return;
    const map = {};
    const phdSubs = submissions.filter(s => PHD_TYPES.includes(s.data_type));
    const sorted = [...phdSubs].sort((a, b) => {
      const ta = new Date(a.submitted_at || 0).getTime();
      const tb = new Date(b.submitted_at || 0).getTime();
      return ta - tb;
    });
    sorted.forEach(sub => {
      const dt = sub.data_type;
      if (!map[dt]) map[dt] = {};
      const year = sub.year;
      if (!year) return;
      if (!map[dt][year]) map[dt][year] = {};
      const cellMap = map[dt][year];
      if (sub.cells && sub.cells.length > 0) {
        sub.cells.forEach(cell => {
          cellMap[cell.field_key] = {
            value: cell.value,
            status: cell.status,
            rejection_reason: cell.rejection_reason,
            proof_materials: cell.proof_materials || [],
            cell_id: cell.id,
            submission_id: sub.id,
          };
        });
      } else {
        const content = typeof sub.data_content === 'object' ? sub.data_content : {};
        Object.keys(content).forEach(key => {
          if (key !== '年份' && key !== 'year') {
            cellMap[key] = {
              value: content[key],
              status: sub.status,
              rejection_reason: sub.rejection_reason,
              proof_materials: sub.proof_materials || [],
              submission_id: sub.id,
            };
          }
        });
      }
    });
    setExistingData(map);
  }, [submissions, department, refreshKey]);

  // Load Excel template file list
  useEffect(() => {
    if (!department?.id) return;
    setTemplateLoading(true);
    api.get('/templates', { params: { department_id: department.id } })
      .then(res => setTemplates(res.data?.templates || {}))
      .catch(() => { /* 静默失败 */ })
      .finally(() => setTemplateLoading(false));
  }, [department?.id]);

  const handleDownloadTemplate = useCallback(async (category, filename) => {
    try {
      const token = getToken();
      const apiUrl = getApiBaseURL();
      const response = await fetch(`${apiUrl}/templates/download?department_id=${department.id}&category=${encodeURIComponent(category)}&filename=${encodeURIComponent(filename)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Download failed');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (e) {
      message.error(e.message || 'Template download failed');
    }
  }, [department?.id]);

  const getCellStatus = useCallback((dataType, fieldKey) => {
    const yearData = existingData[dataType]?.[selectedYear];
    if (!yearData) return null;
    return yearData[fieldKey] || null;
  }, [existingData, selectedYear]);

  const getCellProofs = useCallback((dataType, fieldKey) => {
    const key = `${dataType}_${fieldKey}`;
    const pendingProofs = cellProofs[key] || [];
    const existing = getCellStatus(dataType, fieldKey);
    const existingProofs = existing?.proof_materials || [];
    return [...existingProofs, ...pendingProofs];
  }, [cellProofs, getCellStatus]);

  const handleRemovePendingProof = (dataType, fieldKey, uploadId) => {
    const key = `${dataType}_${fieldKey}`;
    setCellProofs(prev => ({
      ...prev,
      [key]: (prev[key] || []).filter(p => p.upload_id !== uploadId),
    }));
  };

  const handleRemoveSubmittedProof = async (materialId, dataType, fieldKey) => {
    try {
      await api.delete(`/proof-materials/${materialId}/delete`);
      message.success('Proof material deleted');
      setRefreshKey(k => k + 1);
      if (onRefresh) onRefresh();
    } catch (error) {
      message.error(error.response?.data?.error || 'Delete failed');
    }
  };

  const showProofModal = (dataType, fieldKey) => {
    const proofs = getCellProofs(dataType, fieldKey);
    setProofModalData({ dataType, fieldKey, proofs });
    setProofModalVisible(true);
  };

  const handleDownloadProof = async (materialId, filename) => {
    try {
      const token = getToken();
      const apiUrl = getApiBaseURL();
      const response = await fetch(`${apiUrl}/proof-materials/${materialId}/download`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Download failed');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || 'proof_material';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (e) {
      message.error(e.message || 'Download failed');
    }
  };

  const handleUploadProof = async (dataType, fieldKey, file) => {
    const key = `${dataType}_${fieldKey}`;
    const formData = new FormData();
    formData.append('file', file);
    setUploading(prev => ({ ...prev, [key]: true }));
    try {
      const response = await api.post('/proof-materials/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const { upload_id, files } = response.data;
      const uploadedFile = files?.[0];
      if (upload_id && uploadedFile) {
        setCellProofs(prev => ({
          ...prev,
          [key]: [...(prev[key] || []), {
            upload_id,
            filename: uploadedFile.filename,
            file_type: uploadedFile.file_type,
            field_key: fieldKey,
          }],
        }));
        message.success(`${file.name} uploaded`);
      }
    } catch (error) {
      message.error(error.response?.data?.error || 'Upload failed');
    } finally {
      setUploading(prev => ({ ...prev, [key]: false }));
    }
    return false;
  };

  // ── Submit selected rows (all types in one batch) ──

  const handleSubmitSelected = async () => {
    // Group by dataType
    const groups = {};
    Object.keys(selectedRows).forEach(compositeKey => {
      if (!selectedRows[compositeKey]) return;
      // compositeKey = dataType + '_' + fieldKey
      // dataType may contain underscores (e.g. discipline_team), cannot split by indexOf('_')
      const dt = PHD_TYPES.find(t => compositeKey.startsWith(t + '_'));
      if (!dt) return;
      const fk = compositeKey.substring(dt.length + 1);
      if (!groups[dt]) groups[dt] = [];
      groups[dt].push(fk);
    });

    const dtCount = Object.keys(groups).length;
    if (dtCount === 0) {
      message.warning('Select data rows to submit first');
      return;
    }

    for (const dt of Object.keys(groups)) {
      const fks = groups[dt];
      // Build cells array
      const cells = fks
        .map(fk => {
          const inputVal = cellValues[`${dt}_${fk}`];
          const existing = getCellStatus(dt, fk);
          const val = (inputVal !== null && inputVal !== undefined && inputVal !== '')
            ? inputVal : (existing?.value ?? null);
          return { field_key: fk, value: val };
        })
        .filter(c => c.value !== null && c.value !== undefined && c.value !== '');

      if (cells.length === 0) continue;

      // Check proof materials
      const cellsWithoutProof = cells.filter(c => {
        const key = `${dt}_${c.field_key}`;
        const proofs = cellProofs[key] || [];
        const existing = getCellStatus(dt, c.field_key);
        const existingProofs = existing?.proof_materials || [];
        return proofs.length === 0 && existingProofs.length === 0;
      });

      if (cellsWithoutProof.length > 0) {
        message.warning(`[${dt}] 以下数据项需上传证明材料：${cellsWithoutProof.map(c => c.field_key).join('、')}`);
        return;
      }

      const proofMaterials = [];
      fks.forEach(fk => {
        const key = `${dt}_${fk}`;
        const proofs = cellProofs[key] || [];
        proofs.forEach(p => {
          proofMaterials.push({
            field_key: fk,
            upload_id: p.upload_id,
            filename: p.filename,
            file_type: p.file_type || '',
          });
        });
        const existing = getCellStatus(dt, fk);
        const existingProofs = existing?.proof_materials || [];
        existingProofs.forEach(ep => {
          if (!proofs.some(p => p.upload_id === ep.id)) {
            proofMaterials.push({
              field_key: fk,
              id: ep.id,
              filename: ep.filename,
              file_type: ep.file_type || '',
            });
          }
        });
      });

      setSubmitting(true);
      try {
        await api.post('/submissions/phd/create', {
          department_id: department.id,
          data_type: dt,
          year: selectedYear,
          cells,
          proof_materials: proofMaterials,
        });
      } catch (error) {
        message.error(error.response?.data?.error || `[${dt}] 提交失败`);
        setSubmitting(false);
        return;
      }
    }

    message.success(`${selectedYear}年数据提交成功，等待审核`);

    // Clear entered values and selection state
    setCellValues(prev => {
      const next = { ...prev };
      Object.keys(groups).forEach(dt => {
        groups[dt].forEach(fk => delete next[`${dt}_${fk}`]);
      });
      return next;
    });
    setCellProofs(prev => {
      const next = { ...prev };
      Object.keys(groups).forEach(dt => {
        groups[dt].forEach(fk => delete next[`${dt}_${fk}`]);
      });
      return next;
    });
    setSelectedRows({});
    setSubmitting(false);
    if (onRefresh) onRefresh();
    setRefreshKey(k => k + 1);
  };

  const renderStatusTag = (status) => {
    if (!status) return null;
    const map = {
      pending: { color: 'orange', icon: null, text: '待审核' },
      approved: { color: 'green', icon: null, text: '已通过' },
      rejected: { color: 'red', icon: null, text: '已拒绝' },
    };
    const s = map[status];
    if (!s) return null;
    return <Tag color={s.color} style={{ marginLeft: 8 }}>{s.text}</Tag>;
  };

  const handleYearChange = (val) => {
    if (val === null || val === undefined) return;
    const year = Number(val);
    if (!isNaN(year) && year >= 1900 && year <= 2100) {
      setSelectedYear(year);
    }
    setYearInput(String(val));
  };

  const handleYearBlur = () => {
    const year = Number(yearInput);
    if (isNaN(year) || year < 1900 || year > 2100) {
      setYearInput(String(selectedYear));
      message.warning('请输入有效年份（1900-2100）');
    } else {
      setSelectedYear(year);
    }
  };

  // ── Merge rows across all data types ──

  const allFields = useMemo(() => {
    const result = [];
    phdDataForms.forEach(form => {
      const fields = (form.fields || []).filter(f => f.key !== '年份' && f.key !== 'year');
      fields.forEach(f => {
        result.push({
          dataType: form.key,
          indicatorLabel: form.label,
          fieldKey: f.key,
          fieldLabel: f.label,
        });
      });
    });
    return result;
  }, [phdDataForms]);

  // Compute selectable and selected counts
  const { selectableCount, selectedCount } = useMemo(() => {
    let sel = 0;
    let sCount = 0;
    allFields.forEach(f => {
      const currentVal = cellValues[`${f.dataType}_${f.fieldKey}`];
      const hasCurrentValue = currentVal !== null && currentVal !== undefined && currentVal !== '';
      const existing = getCellStatus(f.dataType, f.fieldKey);
      const hasExisting = existing?.value != null && existing?.value !== '';
      if (hasCurrentValue || hasExisting) sel++;
      if (selectedRows[`${f.dataType}_${f.fieldKey}`]) sCount++;
    });
    return { selectableCount: sel, selectedCount: sCount };
  }, [allFields, cellValues, getCellStatus, selectedRows]);

  // ── Build table rows ──

  const rows = useMemo(() => {
    const result = [];
    phdDataForms.forEach(form => {
      const fields = (form.fields || []).filter(f => f.key !== '年份' && f.key !== 'year');
      fields.forEach(f => {
        const existingCell = getCellStatus(form.key, f.key);
        result.push({
          key: `${form.key}::${f.key}`,
          dataType: form.key,
          indicatorLabel: form.label,
          indicatorRowSpan: 0,
          fieldKey: f.key,
          label: f.label,
          existingValue: existingCell?.value ?? null,
          status: existingCell?.status || null,
          rejectionReason: existingCell?.rejection_reason || null,
        });
      });
    });
    // rowSpan
    for (let i = 0; i < result.length;) {
      const label = result[i].indicatorLabel;
      let j = i + 1;
      while (j < result.length && result[j].indicatorLabel === label) j++;
      result[i].indicatorRowSpan = j - i;
      for (let k = i + 1; k < j; k++) result[k].indicatorRowSpan = 0;
      i = j;
    }
    return result;
  }, [phdDataForms, getCellStatus]);

  // ── Column definitions ──

  const columns = useMemo(() => {
    const allSelectable = selectableCount;
    const allSelected = selectedCount;

    return [
      {
        title: (
          <Checkbox
            disabled={allSelectable === 0}
            checked={allSelected > 0 && allSelected === allSelectable}
            indeterminate={allSelected > 0 && allSelected < allSelectable}
            onChange={(e) => {
              const checked = e.target.checked;
              setSelectedRows(prev => {
                const next = {};
                if (checked) {
                  allFields.forEach(f => {
                    const currentVal = cellValues[`${f.dataType}_${f.fieldKey}`];
                    const hasCurrentValue = currentVal !== null && currentVal !== undefined && currentVal !== '';
                    const existing = getCellStatus(f.dataType, f.fieldKey);
                    const hasExisting = existing?.value != null && existing?.value !== '';
                    if (hasCurrentValue || hasExisting) {
                      next[`${f.dataType}_${f.fieldKey}`] = true;
                    }
                  });
                }
                return next;
              });
            }}
          />
        ),
        key: 'selection',
        width: 40,
        align: 'center',
        render: (_, record) => {
          const compositeKey = `${record.dataType}_${record.fieldKey}`;
          const currentVal = cellValues[compositeKey];
          const hasCurrentValue = currentVal !== null && currentVal !== undefined && currentVal !== '';
          const hasExistingValue = record.existingValue != null && record.existingValue !== '';
          const canSelect = hasCurrentValue || hasExistingValue;
          if (!canSelect) {
            return <span style={{ color: '#d9d9d9' }}>—</span>;
          }
          return (
            <Checkbox
              checked={selectedRows[compositeKey] || false}
              onChange={(e) => {
                setSelectedRows(prev => {
                  const next = { ...prev };
                  if (e.target.checked) {
                    next[compositeKey] = true;
                  } else {
                    delete next[compositeKey];
                  }
                  return next;
                });
              }}
            />
          );
        },
      },
      {
        title: '指标类型',
        key: 'indicator',
        width: 120,
        render: (_, record) => {
          if (record.indicatorRowSpan === 0) {
            return { children: null, props: { rowSpan: 0 } };
          }
          return {
            children: (
              <div style={{ fontWeight: 600, fontSize: 13 }}>{record.indicatorLabel}</div>
            ),
            props: { rowSpan: record.indicatorRowSpan || 1 },
          };
        },
      },
      {
        title: '任务项',
        dataIndex: 'label',
        key: 'label',
        width: 280,
        render: (text, record) => (
          <div style={{
            paddingLeft: isSubField(record.fieldKey) ? 24 : 0,
            display: 'flex',
            alignItems: 'center',
          }}>
            <span style={{ fontSize: 13 }}>{text}</span>
            {renderStatusTag(record.status)}
          </div>
        ),
      },
      {
        title: `数据值 (${selectedYear}年)`,
        dataIndex: 'value',
        key: 'value',
        width: 160,
        render: (_, record) => {
          const compositeKey = `${record.dataType}_${record.fieldKey}`;
          const currentVal = cellValues[compositeKey];
          const hasValue = currentVal !== null && currentVal !== undefined && currentVal !== '';
          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <InputNumber
                style={{ width: '100%' }}
                value={hasValue ? currentVal : (record.existingValue ?? null)}
                onChange={val => setCellValues(prev => ({
                  ...prev,
                  [compositeKey]: val,
                }))}
                placeholder={record.existingValue ? `当前: ${record.existingValue}` : '输入数值'}
                min={0}
                size="small"
              />
              {record.status === 'approved' && (
                <CheckCircleFilled style={{ color: '#52c41a', fontSize: 14 }} />
              )}
              {record.status === 'rejected' && (
                <Tooltip title={record.rejectionReason}>
                  <CloseCircleFilled style={{ color: '#ff4d4f', fontSize: 14 }} />
                </Tooltip>
              )}
            </div>
          );
        },
      },
      {
        title: '证明材料',
        key: 'proof',
        width: 260,
        render: (_, record) => {
          const compositeKey = `${record.dataType}_${record.fieldKey}`;
          const isUploading = uploading[compositeKey];
          const proofs = getCellProofs(record.dataType, record.fieldKey);
          const proofCount = proofs.length;
          // Match templates to this row by explicit field-to-template mapping
          const typeMap = TEMPLATE_FIELD_MAP[record.dataType] || {};
          const rowTemplates = (templates[record.dataType] || []).filter(tpl => {
            const base = tpl.filename
              .replace(/表模版\.xlsx$/, '')
              .replace(/表模板\.xlsx$/, '')
              .replace(/表模\.xlsx$/, '');
            const matchedFields = typeMap[base] || [];
            return matchedFields.includes(record.fieldKey);
          });

          return (
            <div>
              {rowTemplates.length > 0 && (
                <div style={{ marginBottom: 4, paddingBottom: 4, borderBottom: '1px dashed #eee', fontSize: 11 }}>
                  {rowTemplates.map((tpl, i) => (
                    <span key={i}>
                      {i > 0 && <span style={{ margin: '0 4px', color: '#ddd' }}>|</span>}
                      <a onClick={(e) => { e.stopPropagation(); handleDownloadTemplate(tpl.category, tpl.filename); }} style={{ color: '#1677ff', cursor: 'pointer' }}>
                        <DownloadOutlined style={{ fontSize: 11 }} /> {tpl.filename}
                      </a>
                    </span>
                  ))}
                </div>
              )}
              <Space size="small" wrap>
                <Upload
                  showUploadList={false}
                  beforeUpload={(file) => {
                    handleUploadProof(record.dataType, record.fieldKey, file);
                    return false;
                  }}
                  accept=".png,.jpg,.jpeg,.gif,.bmp,.webp,.pdf,.zip,.rar,.7z"
                >
                  <Button
                    size="small"
                    icon={<UploadOutlined />}
                    loading={isUploading}
                    style={{ fontSize: 12 }}
                  >
                    {proofCount > 0 ? `材料(${proofCount})` : '上传'}
                  </Button>
                </Upload>
                {proofCount > 0 && (
                  <Button
                    size="small"
                    icon={<DownloadOutlined />}
                    onClick={() => showProofModal(record.dataType, record.fieldKey)}
                    style={{ fontSize: 12 }}
                  >
                    管理
                  </Button>
                )}
                {record.status === 'rejected' && record.rejectionReason && (
                  <Tooltip title={record.rejectionReason}>
                    <CloseCircleFilled style={{ color: '#ff4d4f' }} />
                  </Tooltip>
                )}
              </Space>
            </div>
          );
        },
      },
    ];
  }, [allFields, cellValues, getCellStatus, selectedRows, selectedYear, uploading, selectableCount, selectedCount, templates, handleDownloadTemplate]);

  if (!phdDataForms || phdDataForms.length === 0) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
          <p>该部门暂无博士授权点数据录入配置</p>
        </div>
      </Card>
    );
  }

  return (
    <div>
      {/* 顶部操作栏 */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Row align="middle" gutter={16}>
          <Col>
            <span style={{ fontWeight: 'bold', marginRight: 8 }}>选择年份：</span>
          </Col>
          <Col>
            <Input
              value={yearInput}
              onChange={e => {
                const v = e.target.value.replace(/\D/g, '').slice(0, 4);
                setYearInput(v);
              }}
              onBlur={handleYearBlur}
              onPressEnter={handleYearBlur}
              style={{ width: 100 }}
              placeholder="输入年份"
              maxLength={4}
            />
          </Col>
          <Col flex="auto">
            <span style={{ color: '#666', fontSize: 13 }}>
              填写 {selectedYear} 年数据，勾选需提交的数据行后点击「提交选中行」
            </span>
          </Col>
          <Col>
            <Space>
              {selectedCount > 0 && <Tag color="purple">已选 {selectedCount} 行</Tag>}
              <Button
                type="primary"
                icon={<SendOutlined />}
                onClick={handleSubmitSelected}
                loading={submitting}
                disabled={selectedCount === 0}
              >
                提交选中行 ({selectedCount})
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* 统一数据录入表 */}
      <Card
        size="small"
        title={
          <Space>
            <span style={{ fontWeight: 600 }}>博士授权点数据录入</span>
            <Tag color="blue">{selectedYear}年</Tag>
          </Space>
        }
      >
        <Table
          columns={columns}
          dataSource={rows}
          rowKey="key"
          pagination={false}
          size="small"
          bordered
        />
      </Card>

      {/* 证明材料管理弹窗 */}
      <Modal
        title="证明材料管理"
        open={proofModalVisible}
        onCancel={() => { setProofModalVisible(false); setProofModalData(null); }}
        footer={[<Button key="close" onClick={() => { setProofModalVisible(false); setProofModalData(null); }}>关闭</Button>]}
        width={600}
        destroyOnClose
      >
        {proofModalData && (
          <div>
            <div style={{ marginBottom: 12 }}>
              <Tag color="blue">{proofModalData.dataType}</Tag>
              <span style={{ fontWeight: 'bold' }}>{proofModalData.fieldKey}</span>
              <span style={{ marginLeft: 8, color: '#666' }}>
                共 {proofModalData.proofs.length} 份材料
              </span>
            </div>
            {proofModalData.proofs.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 20, color: '#999' }}>暂无证明材料</div>
            ) : (
              <Table
                size="small"
                pagination={false}
                rowKey={(r) => r.upload_id || r.id || Math.random()}
                bordered
                columns={[
                  { title: '序号', width: 60, render: (_, __, i) => i + 1 },
                  {
                    title: '文件名', dataIndex: 'filename', key: 'filename',
                    render: (text, record) => (
                      <a style={{ wordBreak: 'break-all' }} onClick={() => handleDownloadProof(record.upload_id || record.id, text)}>
                        {text}
                      </a>
                    ),
                  },
                  { title: '类型', dataIndex: 'file_type', key: 'file_type', width: 80 },
                  {
                    title: '状态', key: 'status', width: 80,
                    render: (_, record) => (
                      record.upload_id ? (
                        <Tag color="orange">待提交</Tag>
                      ) : (
                        <Tag color="green">已提交</Tag>
                      )
                    ),
                  },
                  {
                    title: '操作', key: 'action', width: 100,
                    render: (_, record) => {
                      const existing = getCellStatus(proofModalData?.dataType, proofModalData?.fieldKey);
                      const cellStatus = existing?.status;
                      if (record.upload_id) {
                        return (
                          <Button
                            type="link" danger size="small" icon={<DeleteOutlined />}
                            onClick={() => {
                              handleRemovePendingProof(proofModalData.dataType, proofModalData.fieldKey, record.upload_id);
                              const updatedProofs = getCellProofs(proofModalData.dataType, proofModalData.fieldKey)
                                .filter(p => p.upload_id !== record.upload_id);
                              setProofModalData(prev => ({ ...prev, proofs: updatedProofs }));
                            }}
                          >删除</Button>
                        );
                      }
                      if (cellStatus === 'approved') return null;
                      return (
                        <Button
                          type="link" danger size="small" icon={<DeleteOutlined />}
                          onClick={async () => {
                            await handleRemoveSubmittedProof(record.id, proofModalData.dataType, proofModalData.fieldKey);
                            const updatedProofs = getCellProofs(proofModalData.dataType, proofModalData.fieldKey)
                              .filter(p => p.id !== record.id);
                            setProofModalData(prev => ({ ...prev, proofs: updatedProofs }));
                          }}
                        >删除</Button>
                      );
                    },
                  },
                ]}
                dataSource={proofModalData.proofs}
              />
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default PhdMatrixEntry;
